import { Deserializer } from "jsonapi-serializer";
import { MOB_HID_ENABLED, MOB_HID_REMOTE_RSSI, MOB_HID_REMOTE_TOGGLE, MOB_HID_REVERSE_BYTES, MOB_HID_TAP_RSSI, MOB_HID_TAP_TOGGLE, MOB_HID_TWIST_RSSI, MOB_HID_TWIST_TOGGLE, MOB_STID_BLE_NFC, MOB_STID_CARD_DISTANCE, MOB_STID_CARD_TOGGLE, MOB_STID_ENABLED, MOB_STID_HANDS_FREE_DISTANCE, MOB_STID_HANDS_FREE_TOGGLE, MOB_STID_REMOTE_BUTTON, MOB_STID_REMOTE_DISTANCE, MOB_STID_REMOTE_TOGGLE, MOB_STID_REVERSE_BYTES, MOB_STID_SITE_CODE, MOB_STID_SITE_CODE_TYPE, MOB_STID_SMART_UNLOCK, MOB_STID_TAP_TAP_DISTANCE, MOB_STID_TAP_TAP_TOGGLE, MOB_STID_VCARD_NUM_BYTES, MOB_STID_VCARD_OFFSET, MOB_TX_POWER } from "../constants/element-type";
import { findSettingDataColumnKey, isMultiSelectField } from "../constants/field-ui-type";
import { CREATED, NO_CONTENT, OK } from "../constants/response-status-codes";
import { Translator } from "../contexts/translator";
import { genericError } from "../models/GenericError";
import PresetCustomField from "../models/PresetCustomField";
import { filterPreferencePattern, findMatchingAttribute, namePattern } from "../utilities/regex";
import { isSuccessStatus } from "../utilities/requestResponseValidator";
import { usedByStatus } from "../utilities/UsedByStatus";
import { APIConnector } from "./api/APIConnector";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { getDeviceSettingStatus } from "./utilities/StatusBuilder";

export const apiConnector = new APIConnector();

export const buildPreset = (apiPreset, translate: Translator, apiSettingDatas?, apiElementNames?): PresetCustomField => {
    const newCustom = apiSettingDatas && apiElementNames
        ? apiElementNames.reduce((customFragment, element) => {
            const settingData = apiSettingDatas.find(settingData => settingData['element']['id'] === element['id']);
            const nonEmptyEntity = findMatchingAttribute(settingData ?? {}, filterPreferencePattern);
            let entityName: string = '';

            if (nonEmptyEntity) {
                const entity = findMatchingAttribute(nonEmptyEntity[1] ?? {}, namePattern);
                entityName = entity ? entity[1] : '';
            }

            //handle mutli-select
            const isMultiSelect = isMultiSelectField(element['ui-type']['id']);

            const value = isMultiSelect
                ? apiSettingDatas.filter(sd => sd['element']['id'] === element['id']).map(sd => sd['sd-valstring']).filter(v => v)
                : (settingData?.['sd-valstring'] ?? '');

            const settingDataId = isMultiSelect
                ? apiSettingDatas.filter(sd => sd['element']['id'] === element['id']).map(sd => sd['id']).filter(id => id)
                : (settingData?.['id'] ?? '');

            customFragment[element['e-sysname']] = {
                id: element['id'],
                elementSysname: element['e-sysname'],
                uiTypeSysname: element['ui-type']['ut-name'],
                uiTypeId: element['ui-type']['id'],
                settingId: apiPreset['id'] ?? '',
                value: value,
                settingDataId: settingDataId,
                name: entityName,
                settingDataColumnKey: findSettingDataColumnKey(element['ui-type']['id']),
            }

            return customFragment;
        }, {})
        : {}
    return {
        id: apiPreset['id'],
        name: apiPreset['trn-name'],
        type: apiPreset['setting-type']?.['trn-name'],
        typeId: apiPreset['setting-type']?.['id'],
        usedBy: usedByStatus(apiPreset['s-dist'], translate),
        custom: newCustom,
        sdist: apiPreset['s-dist'],
        status: getDeviceSettingStatus(apiPreset['s-dist'] ? apiPreset['s-dist'] : 0),
        profileId: apiPreset['profile']?.['id'],
        settingIdToCopy: apiPreset['setting-id-to-copy'],
        isDefault: apiPreset['is-default'],
    };
};

export const savePreset = async (newPreset: PresetCustomField, createPreset = true): Promise<any> => {
    try {
        const newRecord = {
            type: "setting",
            attributes: {
                trnName: newPreset.name,
                sDist: newPreset.sdist,
                settingIdToCopy: newPreset.settingIdToCopy,
                id: newPreset.id,
            },
            relationships: {
                profileId: newPreset.profileId,
                columnId: newPreset.typeId,
                settingTypeId: newPreset.typeId,
                triggeredByIds: newPreset.triggeredByIds,
                settingData: buildSettingDataForSetting(newPreset.custom)
            }
        };
        
        const url = ``;
        const settingResponse = await apiConnector.sendData(url, true, newRecord, true)
        return new Promise(async (resolve, reject) => {
            if (settingResponse) {
                if (settingResponse.status === OK
                    || settingResponse.status === CREATED) {
                    try {
                        const settingJson = await settingResponse.json()
                        return resolve({ id: settingJson['attributes']?.['id'], success: true })
                    }
                    catch (exception) {
                        return reject({ status: settingResponse.status, id: newPreset.id, message: genericError, success: false });
                    }
                }
                else {
                    if (settingResponse.body) {
                        if (settingResponse.statusText) {
                            const content = await settingResponse.json();
                            return reject({ status: settingResponse.status, id: newPreset.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
                        }
                        return reject({ status: settingResponse.status, id: newPreset.id, message: genericError, success: false });
                    }
                    return reject({ status: settingResponse.status, id: newPreset.id, message: genericError, success: false });
                }
            }
            else {
                return reject({ status: 500, id: newPreset.id, message: genericError, success: false });
            }
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getPresetSettingById = async (id: string, translate): Promise<PresetCustomField> => {
    try {
        const settingFields = `fields[setting]=trnName,sDist,settingType,profile,settingIdToCopy,settingDatas`;
        const settingDataInclude = "settingDatas.accessTimePattern,settingDatas.area,settingDatas.eventType,settingDatas.building,settingDatas.profile," +
            "settingDatas.deviceImprox,settingDatas.zone,settingDatas.location,settingDatas.company," +
            "settingDatas.timePattern,settingDatas.site,settingDatas.department,report,settingDatas.element.uiType,settingDatas.accessGroup," +
            "settingDatas.longMessage,settingDatas.master,settingDatas.masterDetail";
        const profileFields = `fields[profile]=trnName`;
        const settingTypeFields = `fields[settingType]=trnName`;
        const settingDataFields = `fields[settingData]=sdValstring,element,department,company,site,accessTimePattern,zone,area,location,profile,deviceImprox,accessGroup,timePattern,longMessage,master,masterDetail`;
        const departmentFields = "fields[department]=deptName";
        const companyFields = "fields[company]=compName";
        const siteFields = "fields[site]=siteName";
        const accessTimePatternFields = "fields[accessTimePattern]=atpName";
        const timePatternFields = "fields[timePattern]=tpName";
        const zoneFields = "fields[zone]=zoneName";
        const areaFields = "fields[area]=areaName";
        const locationFields = "fields[location]=locName";
        const deviceFields = "fields[deviceImprox]=devName";
        const uiTypeFields = "fields[uiType]=utName";
        const elementFields = "fields[element]=eSysname,uiType";
        const accessGroupFields = "fields[accessGroup]=agName";
        const longMessageFields = "fields[longMessage]=lmMessage";
        const masterFields = "fields[master]=mstDisplayname";
        const masterDetailFields = "fields[masterDetail]=mdValString";
        const include = `include=settingType,profile,${settingDataInclude}`;
        const url = `setting/${id}?${settingFields}&${settingTypeFields}&${profileFields}&${settingDataFields}&${departmentFields}&${companyFields}&${siteFields}&${accessTimePatternFields}&${zoneFields}&${areaFields}&${locationFields}&${deviceFields}&${elementFields}&${accessGroupFields}&${timePatternFields}&${uiTypeFields}&${longMessageFields}&${masterFields}&${masterDetailFields}&${include}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            // return the id for element relationship to be used later.
            new Deserializer({
                element: {
                    valueForRelationship: (relationship) => {
                        return { id: relationship.id }
                    }
                }
            }).deserialize(json, async (error: any, apiPreset: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }
                const presetTypeId = apiPreset['setting-type']?.['id'];
                const elementsUrl = `element?include=settingType,uiType&filter=settingType.id==${presetTypeId}`;
                const elementsResponse = await apiConnector.fetchData(elementsUrl);
                const elementsJson = await elementsResponse.json();

                new Deserializer().deserialize(elementsJson, async (error: any, apiElementNames: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }
                    const preset: PresetCustomField = buildPreset(apiPreset, translate, apiPreset['setting-datas'], apiElementNames);

                    return resolve(preset);
                });
            });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getPresetTypeNameById = async (id: string): Promise<string> => {
    try {
        const url = `settingType/${id}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (error: any, apiPresetInput: any) => {
                if (error) {
                    return reject({ status: response.status, message: genericError });
                }

                return resolve(apiPresetInput['trn-name']);
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const deletePresetSetting = async (id: string): Promise<{ id: string, success: boolean }> => {
    try {
        const url = `setting/${id}`;
        const presetSetting = {
            data: {
                type: "setting",
                id: id
            }
        }
        const response = await apiConnector.deleteData(url, presetSetting)

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true })
            }

            const json = await response.json();

            return reject({ status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail) });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const buildBioSettingData = (settingDataId: any, settingData: any) => {
    let settingDataValue = settingData["value"];

    if (settingDataValue == false) {
        settingDataValue = '0';
    }
    else if (settingDataValue == true) {
        settingDataValue = '1';
    }

    return {
        type: "settingData",
        id: settingDataId ? settingDataId : 0,
        attributes: {
            sdValstring: settingDataValue
        },
        relationships: {
            element: {
                data: {
                    type: "element",
                    id: settingData['id']
                }
            }
        }
    };
}

export const updateBioPreset = async (newPreset: PresetCustomField): Promise<any> => {
    try {
        const settingDataUrl = 'settingData'
        let settingDataResponses: Response[] = new Array(0);

        const custom = newPreset.custom || {};
        const multiAuthFace = custom['BIO_CID_MULTI_FACTOR_AUTH_FACE'];
        const multiAuthAccess = custom['BIO_CID_MULTI_FACTOR_AUTH_ACCESS'];

        const isMultiAuthEnabled =
            (multiAuthFace && Number(multiAuthFace.value) === 1) ||
            (multiAuthAccess && Number(multiAuthAccess.value) === 1);

        if (isMultiAuthEnabled && custom['BIO_CID_WIEGAND_OUT_MODE']) {
            custom['BIO_CID_WIEGAND_OUT_MODE'].value = 'ID_TRANSMISSION';
        }
        //unsure why this one doesnt need a vlaue- need to be iD which is changing.
        if (isMultiAuthEnabled && custom['BIO_CID_OSDP_OUTPUT_MODE']) {
            custom['BIO_CID_OSDP_OUTPUT_MODE'].value = '';
        }

        const settingDataPromises = newPreset.custom
            ? Object.values<any>(newPreset.custom)
                .filter(settingData =>
                    (!!settingData['value'] || !!settingData['settingDataId']))
                .map((settingData) => {
                    const settingDataId = settingData["settingDataId"] ?? 0;
                    const newData = buildBioSettingData(settingDataId, settingData)
                    const newSettingData = {
                        data: (+settingDataId) > 0 ? newData : [newData]
                    }
                    const updatedUrl = (+settingDataId) > 0 ? `${settingDataUrl}/${settingDataId}` : settingDataUrl;
                    return apiConnector.sendData(updatedUrl, !((+settingDataId) > 0), newSettingData);
                }) : new Array(0);
        if (settingDataPromises.length > 0) {
            settingDataResponses = await Promise.all(settingDataPromises.flat());
        }
        return new Promise((resolve, reject) => {
            if (settingDataResponses.filter(response => !response || (isSuccessStatus(response.status) == false)).length > 0) {
                return reject({ id: '', success: false });
            }
            else {
                return resolve({ id: '', success: true });
            }
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getReaderPresetMobileSettings = async (profileId: string | number): Promise<any> => {
    try {
        const filter = `filter=setting.profile.id==${profileId};setting.settingType.stSysname==MOB_*`;
        const settingTypeFields = "fields[settingType]=stSysname";
        const elementFields = "fields[element]=eSysname";
        const settingDataFields = "fields[settingData]=sdValstring,element";
        const settingFields = "fields[setting]=profile,settingType";
        const include = "include=setting,setting.profile,setting.settingType,element";
        const pagination = "page[size]=100";
        const url = `settingData?${include}&${settingDataFields}&${settingTypeFields}&${elementFields}&${settingFields}&${filter}&${pagination}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, settingDatas: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                let stidMobileValues: any = {}, hidMobileValues: any = {}, txPower: string = '';
                const settingDatasRefs: { eSysname: string, id: string, sdValstring: string }[] = [];

                // Mapping object for e-sysname to handler functions
                const handlers: Record<string, (settingData: any) => void> = {
                    [MOB_HID_ENABLED]: (data) => hidMobileValues.enabled = data['sd-valstring'],
                    [MOB_HID_TWIST_TOGGLE]: (data) => hidMobileValues.twistAndGo = data['sd-valstring'] !== '0',
                    [MOB_HID_TWIST_RSSI]: (data) => hidMobileValues.twistAndGoRssi = parseInt(data['sd-valstring']),
                    [MOB_HID_TAP_TOGGLE]: (data) => hidMobileValues.tap = data['sd-valstring'] !== '0',
                    [MOB_HID_TAP_RSSI]: (data) => hidMobileValues.tapRssi = parseInt(data['sd-valstring']),
                    [MOB_HID_REMOTE_TOGGLE]: (data) => hidMobileValues.remote = data['sd-valstring'] !== '0',
                    [MOB_HID_REMOTE_RSSI]: (data) => hidMobileValues.remoteRssi = parseInt(data['sd-valstring']),
                    [MOB_HID_REVERSE_BYTES]: (data) => hidMobileValues.reverseBytes = data['sd-valstring'] !== '0',
                    [MOB_STID_ENABLED]: (data) => stidMobileValues.enabled = data['sd-valstring'],
                    [MOB_STID_CARD_TOGGLE]: (data) => stidMobileValues.card = data['sd-valstring'] !== '0',
                    [MOB_STID_CARD_DISTANCE]: (data) => stidMobileValues.cardOption = data['sd-valstring'],
                    [MOB_STID_HANDS_FREE_TOGGLE]: (data) => stidMobileValues.handsFree = data['sd-valstring'] !== '0',
                    [MOB_STID_HANDS_FREE_DISTANCE]: (data) => stidMobileValues.handsFreeOption = data['sd-valstring'],
                    [MOB_STID_TAP_TAP_TOGGLE]: (data) => stidMobileValues.tapTap = data['sd-valstring'] !== '0',
                    [MOB_STID_TAP_TAP_DISTANCE]: (data) => stidMobileValues.tapTapOption = data['sd-valstring'],
                    [MOB_STID_REMOTE_TOGGLE]: (data) => stidMobileValues.remote = data['sd-valstring'] !== '0',
                    [MOB_STID_REMOTE_DISTANCE]: (data) => stidMobileValues.remoteOption = data['sd-valstring'],
                    [MOB_STID_SMART_UNLOCK]: (data) => stidMobileValues.smartUnlock = data['sd-valstring'] !== '0',
                    [MOB_STID_VCARD_NUM_BYTES]: (data) => stidMobileValues.numberOfBytes = data['sd-valstring'],
                    [MOB_STID_VCARD_OFFSET]: (data) => stidMobileValues.offset = data['sd-valstring'],
                    [MOB_STID_REVERSE_BYTES]: (data) => stidMobileValues.reverseBytes = data['sd-valstring'] !== '0',
                    [MOB_STID_BLE_NFC]: (data) => stidMobileValues.bleNfcKey = data['sd-valstring'],
                    [MOB_STID_REMOTE_BUTTON]: (data) => stidMobileValues.remoteButton = data['sd-valstring'],
                    [MOB_STID_SITE_CODE]: (data) => stidMobileValues.siteCode = data['sd-valstring'],
                    [MOB_STID_SITE_CODE_TYPE]: (data) => stidMobileValues.siteCodeType = data['sd-valstring'],
                    [MOB_TX_POWER]: (data) => txPower = data['sd-valstring'],
                };

                // Process each settingData
                settingDatas.forEach((settingData) => {
                    const eSysname = settingData['element']['e-sysname'];
                    if (handlers[eSysname]) {
                        handlers[eSysname](settingData);
                        settingDatasRefs.push({
                            eSysname,
                            id: settingData['id'],
                            sdValstring: settingData['sd-valstring']
                        });
                    }
                });

                // If handsFree is enabled, set the mode to hands free otherwise set it to standard
                stidMobileValues.modes = stidMobileValues.handsFree ? "handsFree" : "standard";

                return resolve({ stidMobileValues, hidMobileValues, settingDatasRefs, power: txPower });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const buildSettingDataForSetting = (custom: any): any[] => {
    const filtered = Object.values(custom).filter((newSettingData: any) => !!newSettingData.value || !!newSettingData.settingDataId);
    const settingDatas = filtered.map((newSettingData: any) => {

        // For when we have multi select values. For example, inhibit scanner allow selecting multiple inhibit readers, time pattern.
        if (Array.isArray(newSettingData.value)) {
            const settingDatas = newSettingData.value.map((sdValstring: string, index: number) => {
                return {
                    id: parseInt(newSettingData.settingDataId[index] ? newSettingData.settingDataId[index] : '0'),
                    elementId: newSettingData.id,
                    sdValstring: sdValstring,
                    [newSettingData.settingDataColumnKey]: sdValstring,
                };
            });

            return [...settingDatas];
        }

        // For when we have a single value which normally is the case
        let newSettingDataColumnKey: { [key: string]: any } | undefined = undefined;

        if (newSettingData.settingDataColumnKey) {
            newSettingDataColumnKey = { [newSettingData.settingDataColumnKey]: newSettingData.value }
        }

        return {
            id: parseInt(newSettingData.settingDataId ? newSettingData.settingDataId : '0'),
            elementId: newSettingData.id,
            sdValstring: newSettingData.value,
            ...newSettingDataColumnKey
        };
    });

    return settingDatas.flat();
};
